// ===== 回收站服务（软删除 + 恢复 + 7 天自动清除） =====
import { KEYS, dbGet, dbSet, dbSetChecked } from './db';

export const TRASH_RETENTION_DAYS = 7; // 默认 7 天内自动清除

export interface TrashItem {
  trashId: string;      // 回收站条目唯一 ID
  sourceKey: string;    // 来源存储 key（如 KEYS.customers）
  sourceLabel: string;  // 中文来源（客户 / 供应商 / 商品 / 销售单 ...）
  entity: unknown;      // 被删除的完整对象
  summary: string;      // 展示摘要（名称等）
  deletedAt: string;    // 删除时间 ISO
  /** 旧数据默认 IndexedDB；业务记录仍存 localStorage 时显式标识，才能正确恢复。 */
  storage?: 'idb' | 'localStorage';
}

async function readTrash(): Promise<TrashItem[]> {
  const list = await dbGet<TrashItem[]>(KEYS.trash);
  return Array.isArray(list) ? list : [];
}

async function writeTrash(list: TrashItem[]) {
  await dbSet(KEYS.trash, list);
}

/** 删除数据时移入回收站 */
export async function addToTrash(
  sourceKey: string,
  sourceLabel: string,
  entity: unknown,
  summary: string,
  storage: 'idb' | 'localStorage' = 'idb',
): Promise<void> {
  const list = await readTrash();
  const item: TrashItem = {
    trashId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    sourceKey,
    sourceLabel,
    entity,
    summary,
    deletedAt: new Date().toISOString(),
    storage,
  };
  list.push(item);
  await writeTrash(list);
}

/** 列出回收站全部条目（按删除时间倒序） */
export async function listTrash(): Promise<TrashItem[]> {
  const list = await readTrash();
  return list.sort((a, b) => b.deletedAt.localeCompare(a.deletedAt));
}

/** 从回收站恢复条目：把 entity 写回目标存储并移除回收站记录 */
export async function restoreFromTrash(trashId: string): Promise<TrashItem | null> {
  const list = await readTrash();
  const item = list.find((t) => t.trashId === trashId);
  if (!item) return null;
  let restored = false;
  try {
    if (item.storage === 'localStorage') {
      let target: unknown[] = [];
      try {
        const raw = localStorage.getItem(item.sourceKey);
        const parsed: unknown = raw ? JSON.parse(raw) : [];
        target = Array.isArray(parsed) ? parsed : [];
      } catch { /* 损坏的旧值按空列表恢复，保住回收站记录本身。 */ }
      localStorage.setItem(item.sourceKey, JSON.stringify([...target, item.entity]));
    } else {
      // 目标存储可能是数组（列表）——追加回末尾
      const target = await dbGet<unknown[]>(item.sourceKey);
      if (Array.isArray(target)) {
        await dbSetChecked(item.sourceKey, [...target, item.entity]);
      } else if (target == null) {
        await dbSetChecked(item.sourceKey, [item.entity]);
      } else {
        await dbSetChecked(item.sourceKey, item.entity);
      }
    }
    restored = true;
  } catch (e) {
    console.error('恢复回收站条目时目标存储写入失败', item.sourceKey, e);
    throw new Error('恢复失败：目标数据未能保存，请重试或联系管理员');
  }
  if (restored) {
    await writeTrash(list.filter((t) => t.trashId !== trashId));
  }
  return item;
}

/** 从回收站永久删除（不可恢复） */
export async function permanentlyDelete(trashId: string): Promise<void> {
  const list = await readTrash();
  await writeTrash(list.filter((t) => t.trashId !== trashId));
}

/** 清空回收站 */
export async function clearTrash(): Promise<void> {
  await writeTrash([]);
}

/** 自动清除超过 7 天的记录，返回清除数量 */
export async function purgeExpiredTrash(): Promise<number> {
  const list = await readTrash();
  const cutoff = Date.now() - TRASH_RETENTION_DAYS * 24 * 60 * 60 * 1000;
  const remaining = list.filter((t) => new Date(t.deletedAt).getTime() > cutoff);
  if (remaining.length !== list.length) {
    await writeTrash(remaining);
    return list.length - remaining.length;
  }
  return 0;
}

/** 计算条目剩余可恢复天数（>0 表示还可恢复） */
export function trashRemainingDays(item: TrashItem): number {
  const elapsed = Date.now() - new Date(item.deletedAt).getTime();
  const days = Math.ceil((TRASH_RETENTION_DAYS * 24 * 60 * 60 * 1000 - elapsed) / (24 * 60 * 60 * 1000));
  return Math.max(0, days);
}
